import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Spin, Tooltip } from 'antd';
import type { NamePath } from '@xbcb/ui-types';
import { useBundle } from '@amzn/react-arb-tools';
import { safeGetMessage } from '@xbcb/ui-utils';
import { formatHTS } from '@xbcb/hts-client-utils';
import { AdditionalHtsType } from '@xbcb/trade-data-types';
import { clearFields } from 'libs/clearFields';
import {
  useArrivalDate,
  useDebouncedCallback,
  useHtsQuery,
  usePrevious,
} from 'libs/hooks';
import HTSNumberV2 from 'components/HTSNumberV2';
import { HtsObject } from 'types';
import { ModeOfTransport } from '__generated__/graphql';
import {
  isEvaluatedDutyDisplayEnabled,
  isTariff232AutomationFeatureEnabled,
} from 'featureFlags';
import { useTariffEvaluation } from './useTariffEvaluation';
import {
  getRowLabel,
  formatDutyRate,
  computeDutyValue,
  formatAdValoremPercent,
  getSection232CappedAdValoremRate,
  getEvaluatedDuty,
} from './utils';
import {
  ProductValueRow,
  TariffRow,
  TariffRowLabel,
  TariffRowContent,
  DutyInfo,
  RemoveButton,
} from './styles';

export interface LineItemTariffDisplayV2Props {
  tariffFullNamePath: NamePath;
  tariffLocalNamePath: NamePath;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  showValidateButton?: boolean;
  validateLoading?: boolean;
  onValidateHts?: () => void;
  lineIndex?: number;
  lineCount?: number;
}

const SECTION_232_METAL_TYPES = new Set([
  AdditionalHtsType.SECTION_232_METAL,
  AdditionalHtsType.SECTION_232_METAL_EXCLUSION,
]);

const LINE_LEVEL_PRESERVE = [
  'tariffs',
  'manufacturer',
  'origin',
  'description',
  'poNumber',
  'externalReference',
  'censusWarnings',
  // Preserve the compliance checks (reasonability / HTS classification) on an
  // HTS clear or reselect; the useHtsClassification invalidate effect then marks
  // reasonability as USER_MANUAL_OVERRIDE. Clearing them here instead wipes the
  // reasonability status, leaving it blank.
  'complianceChecks',
  'steelRequirements',
  'aluminumRequirements',
  'copperRequirements',
  'doesNotContainSteel',
  'doesNotContainAluminum',
  'doesNotContainCopper',
];

export const LineItemTariffDisplayV2: React.FC<
  LineItemTariffDisplayV2Props
> = ({
  tariffFullNamePath,
  tariffLocalNamePath,
  required,
  disabled,
  readOnly,
  showValidateButton,
  validateLoading,
  onValidateHts,
}) => {
  const [bundle] = useBundle('components.LineItemTariffDisplayV2');
  const form = Form.useFormInstance();
  const arrivalDate = useArrivalDate();
  const { queryEffectiveUsHtss, getEffectiveUsHtssData } = useHtsQuery();
  const htsData = getEffectiveUsHtssData(arrivalDate);

  const tariffs = Form.useWatch(tariffFullNamePath, form);
  useEffect(() => {
    if (Array.isArray(tariffs) && tariffs.length === 0) {
      form.setFieldValue(tariffFullNamePath, [{}]);
    }
  }, [tariffs, form, tariffFullNamePath]);

  const lineFullNamePath = useMemo(
    () => (tariffFullNamePath as (string | number)[]).slice(0, -1),
    [tariffFullNamePath],
  );

  const linesKeyIdx = (tariffFullNamePath as (string | number)[]).lastIndexOf(
    'lines',
  );
  const productValuePath = useMemo(
    () =>
      linesKeyIdx >= 0
        ? [
            ...(tariffFullNamePath as (string | number)[]).slice(
              0,
              linesKeyIdx,
            ),
            'totalValue',
            'value',
          ]
        : [],
    [tariffFullNamePath, linesKeyIdx],
  );
  const rawProductValue = Form.useWatch(productValuePath, form);
  const productValue: number = Number(rawProductValue) || 0;

  const originNamePath = useMemo(
    () => [...lineFullNamePath, 'origin', 'countryCode'],
    [lineFullNamePath],
  );
  const originCountryCode: string | undefined = Form.useWatch(
    originNamePath,
    form,
  );

  const modeOfTransport: ModeOfTransport | undefined = Form.useWatch(
    ['conveyance', 'modeOfTransport'],
    form,
  );

  const netWeightPath = useMemo(
    () =>
      linesKeyIdx >= 0
        ? [
            ...(tariffFullNamePath as (string | number)[]).slice(
              0,
              linesKeyIdx,
            ),
            'netWeight',
            'value',
          ]
        : [],
    [tariffFullNamePath, linesKeyIdx],
  );
  const netWeightValue: number | undefined = Form.useWatch(netWeightPath, form);

  const steelNetWeightPath = useMemo(
    () => [...lineFullNamePath, 'steelRequirements', 'netSteelWeight', 'value'],
    [lineFullNamePath],
  );
  const steelNetWeight: number | undefined = Form.useWatch(
    steelNetWeightPath,
    form,
  );

  const aluminumNetWeightPath = useMemo(
    () => [
      ...lineFullNamePath,
      'aluminumRequirements',
      'netAluminumWeight',
      'value',
    ],
    [lineFullNamePath],
  );
  const aluminumNetWeight: number | undefined = Form.useWatch(
    aluminumNetWeightPath,
    form,
  );

  const copperNetWeightPath = useMemo(
    () => [
      ...lineFullNamePath,
      'copperRequirements',
      'netCopperWeight',
      'value',
    ],
    [lineFullNamePath],
  );
  const copperNetWeight: number | undefined = Form.useWatch(
    copperNetWeightPath,
    form,
  );

  const doesNotContainSteelPath = useMemo(
    () => [...lineFullNamePath, 'doesNotContainSteel'],
    [lineFullNamePath],
  );
  const doesNotContainSteel: boolean | undefined = Form.useWatch(
    doesNotContainSteelPath,
    form,
  );

  const doesNotContainAluminumPath = useMemo(
    () => [...lineFullNamePath, 'doesNotContainAluminum'],
    [lineFullNamePath],
  );
  const doesNotContainAluminum: boolean | undefined = Form.useWatch(
    doesNotContainAluminumPath,
    form,
  );

  const doesNotContainCopperPath = useMemo(
    () => [...lineFullNamePath, 'doesNotContainCopper'],
    [lineFullNamePath],
  );
  const doesNotContainCopper: boolean | undefined = Form.useWatch(
    doesNotContainCopperPath,
    form,
  );

  const steelCountryPath = useMemo(
    () => [
      ...lineFullNamePath,
      'steelRequirements',
      'steelCountryOfMeltAndPour',
    ],
    [lineFullNamePath],
  );
  const steelCountry: string | undefined = Form.useWatch(
    steelCountryPath,
    form,
  );

  const aluminumPrimarySmeltPath = useMemo(
    () => [
      ...lineFullNamePath,
      'aluminumRequirements',
      'aluminumPrimaryCountryOfSmelt',
    ],
    [lineFullNamePath],
  );
  const aluminumPrimarySmelt: string | undefined = Form.useWatch(
    aluminumPrimarySmeltPath,
    form,
  );

  const aluminumSecondarySmeltPath = useMemo(
    () => [
      ...lineFullNamePath,
      'aluminumRequirements',
      'aluminumSecondaryCountryOfSmelt',
    ],
    [lineFullNamePath],
  );
  const aluminumSecondarySmelt: string | undefined = Form.useWatch(
    aluminumSecondarySmeltPath,
    form,
  );

  const aluminumCountryCastPath = useMemo(
    () => [
      ...lineFullNamePath,
      'aluminumRequirements',
      'aluminumCountryOfCast',
    ],
    [lineFullNamePath],
  );
  const aluminumCountryCast: string | undefined = Form.useWatch(
    aluminumCountryCastPath,
    form,
  );

  const copperPrimarySmeltPath = useMemo(
    () => [
      ...lineFullNamePath,
      'copperRequirements',
      'copperPrimaryCountryOfSmelt',
    ],
    [lineFullNamePath],
  );
  const copperPrimarySmelt: string | undefined = Form.useWatch(
    copperPrimarySmeltPath,
    form,
  );

  const copperSecondarySmeltPath = useMemo(
    () => [
      ...lineFullNamePath,
      'copperRequirements',
      'copperSecondaryCountryOfSmelt',
    ],
    [lineFullNamePath],
  );
  const copperSecondarySmelt: string | undefined = Form.useWatch(
    copperSecondarySmeltPath,
    form,
  );

  const copperCountryCastPath = useMemo(
    () => [...lineFullNamePath, 'copperRequirements', 'copperCountryOfCast'],
    [lineFullNamePath],
  );
  const copperCountryCast: string | undefined = Form.useWatch(
    copperCountryCastPath,
    form,
  );

  const { runTariffEvaluation, clearEvaluatedCharges, evaluating } =
    useTariffEvaluation({
      form,
      tariffFullNamePath,
      originCountryCode,
      arrivalDate,
      modeOfTransport,
    });
  // The primary HTS is the last tariff; its rate is the Section 232 cap base.
  const primaryTariff = tariffs?.[tariffs?.length - 1];
  const primaryTariffCode = primaryTariff?.htsNumber;
  const primaryHtsObj = primaryTariffCode
    ? htsData[formatHTS(primaryTariffCode)]
    : undefined;

  const lineValuePath = useMemo(
    () => [...lineFullNamePath, 'value'],
    [lineFullNamePath],
  );
  const lineValue: number | undefined = Form.useWatch(lineValuePath, form);

  const lineAssistPath = useMemo(
    () => [...lineFullNamePath, 'assist'],
    [lineFullNamePath],
  );
  const lineAssist: number | undefined = Form.useWatch(lineAssistPath, form);

  const unitReportingQuantitiesKey = JSON.stringify(
    tariffs?.[tariffs?.length - 1]?.unitReportingQuantities ?? null,
  );

  const prevPrimaryTariffCode = usePrevious(primaryTariffCode);
  const prevOriginCountryCode = usePrevious(originCountryCode);
  const prevArrivalDate = usePrevious(arrivalDate);
  const prevModeOfTransport = usePrevious(modeOfTransport);
  const prevNetWeightValue = usePrevious(netWeightValue);
  const prevSteelNetWeight = usePrevious(steelNetWeight);
  const prevAluminumNetWeight = usePrevious(aluminumNetWeight);
  const prevCopperNetWeight = usePrevious(copperNetWeight);
  const prevDoesNotContainSteel = usePrevious(doesNotContainSteel);
  const prevDoesNotContainAluminum = usePrevious(doesNotContainAluminum);
  const prevDoesNotContainCopper = usePrevious(doesNotContainCopper);
  const prevSteelCountry = usePrevious(steelCountry);
  const prevAluminumPrimarySmelt = usePrevious(aluminumPrimarySmelt);
  const prevAluminumSecondarySmelt = usePrevious(aluminumSecondarySmelt);
  const prevAluminumCountryCast = usePrevious(aluminumCountryCast);
  const prevCopperPrimarySmelt = usePrevious(copperPrimarySmelt);
  const prevCopperSecondarySmelt = usePrevious(copperSecondarySmelt);
  const prevCopperCountryCast = usePrevious(copperCountryCast);
  const prevLineValue = usePrevious(lineValue);
  const prevLineAssist = usePrevious(lineAssist);
  const prevUnitReportingQuantitiesKey = usePrevious(
    unitReportingQuantitiesKey,
  );

  const hasSection232Metal = Boolean(
    tariffs?.some(
      (tariff: { tariffType?: string; additionalTariffType?: string }) =>
        SECTION_232_METAL_TYPES.has(tariff?.tariffType as AdditionalHtsType) ||
        SECTION_232_METAL_TYPES.has(
          tariff?.additionalTariffType as AdditionalHtsType,
        ),
    ),
  );

  const hasMetalWeight =
    steelNetWeight != null ||
    aluminumNetWeight != null ||
    copperNetWeight != null;

  const netWeightChanged =
    hasSection232Metal &&
    hasMetalWeight &&
    prevNetWeightValue !== netWeightValue;

  const section232MetalWeightsChanged =
    isTariff232AutomationFeatureEnabled &&
    hasSection232Metal &&
    netWeightValue != null &&
    (prevSteelNetWeight !== steelNetWeight ||
      prevAluminumNetWeight !== aluminumNetWeight ||
      prevCopperNetWeight !== copperNetWeight);

  const section232MetalFlagsChanged =
    isTariff232AutomationFeatureEnabled &&
    hasSection232Metal &&
    (prevDoesNotContainSteel !== doesNotContainSteel ||
      prevDoesNotContainAluminum !== doesNotContainAluminum ||
      prevDoesNotContainCopper !== doesNotContainCopper);

  const section232MetalCountriesChanged =
    isTariff232AutomationFeatureEnabled &&
    hasSection232Metal &&
    (prevSteelCountry !== steelCountry ||
      prevAluminumPrimarySmelt !== aluminumPrimarySmelt ||
      prevAluminumSecondarySmelt !== aluminumSecondarySmelt ||
      prevAluminumCountryCast !== aluminumCountryCast ||
      prevCopperPrimarySmelt !== copperPrimarySmelt ||
      prevCopperSecondarySmelt !== copperSecondarySmelt ||
      prevCopperCountryCast !== copperCountryCast);

  const transportParamsChanged =
    prevOriginCountryCode !== originCountryCode ||
    prevArrivalDate !== arrivalDate ||
    prevModeOfTransport !== modeOfTransport;

  const metalWeightsChanged = netWeightChanged || section232MetalWeightsChanged;

  const customsValueInputsChanged =
    isEvaluatedDutyDisplayEnabled &&
    (prevLineValue !== lineValue ||
      prevLineAssist !== lineAssist ||
      prevUnitReportingQuantitiesKey !== unitReportingQuantitiesKey);

  const debouncedRunTariffEvaluation = useDebouncedCallback(
    (htsCode: string) => {
      void runTariffEvaluation(htsCode);
    },
    800,
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!primaryTariffCode || !originCountryCode) return;
    if (prevPrimaryTariffCode === undefined) return;

    if (
      transportParamsChanged ||
      section232MetalFlagsChanged ||
      section232MetalCountriesChanged
    ) {
      void runTariffEvaluation(primaryTariffCode);
      return;
    }

    if (metalWeightsChanged || customsValueInputsChanged) {
      clearEvaluatedCharges();
      debouncedRunTariffEvaluation(primaryTariffCode);
    }
  }, [
    primaryTariffCode,
    originCountryCode,
    arrivalDate,
    modeOfTransport,
    netWeightValue,
    steelNetWeight,
    aluminumNetWeight,
    copperNetWeight,
    doesNotContainSteel,
    doesNotContainAluminum,
    doesNotContainCopper,
    steelCountry,
    aluminumPrimarySmelt,
    aluminumSecondarySmelt,
    aluminumCountryCast,
    copperPrimarySmelt,
    copperSecondarySmelt,
    copperCountryCast,
    lineValue,
    lineAssist,
    unitReportingQuantitiesKey,
    prevPrimaryTariffCode,
    prevArrivalDate,
    prevOriginCountryCode,
    prevModeOfTransport,
    prevNetWeightValue,
    prevSteelNetWeight,
    prevAluminumNetWeight,
    prevCopperNetWeight,
    prevDoesNotContainSteel,
    prevDoesNotContainAluminum,
    prevDoesNotContainCopper,
    prevSteelCountry,
    prevAluminumPrimarySmelt,
    prevAluminumSecondarySmelt,
    prevAluminumCountryCast,
    prevCopperPrimarySmelt,
    prevCopperSecondarySmelt,
    prevCopperCountryCast,
    prevLineValue,
    prevLineAssist,
    prevUnitReportingQuantitiesKey,
  ]);

  const [htsQueryLoading, setHtsQueryLoading] = useState(false);
  const loading = htsQueryLoading || evaluating;

  const handlePrimarySelect = useCallback(
    async (value: string) => {
      if (!value) return;
      form.setFieldValue(tariffFullNamePath, [{ htsNumber: value }]);
      clearFields({
        form,
        fullNamePath: lineFullNamePath,
        exclude: LINE_LEVEL_PRESERVE,
      })();
      clearEvaluatedCharges();
      setHtsQueryLoading(true);
      await queryEffectiveUsHtss([value], arrivalDate);
      setHtsQueryLoading(false);
      void runTariffEvaluation(value);
    },
    [
      form,
      tariffFullNamePath,
      lineFullNamePath,
      arrivalDate,
      queryEffectiveUsHtss,
      runTariffEvaluation,
      clearEvaluatedCharges,
    ],
  );

  const handleSecondarySelect = useCallback(
    (index: number) => async (value: string) => {
      if (!value) return;
      form.setFields([
        {
          name: [...tariffFullNamePath, index, 'tariffType'],
          value: undefined,
        },
        {
          name: [...tariffFullNamePath, index, 'additionalTariffType'],
          value: undefined,
        },
      ]);
      clearEvaluatedCharges();
      const formatted = formatHTS(value);
      if (!htsData[formatted]) {
        await queryEffectiveUsHtss([value], arrivalDate);
      }
      if (isEvaluatedDutyDisplayEnabled && primaryTariffCode) {
        void runTariffEvaluation(primaryTariffCode);
      }
    },
    [
      form,
      tariffFullNamePath,
      htsData,
      arrivalDate,
      queryEffectiveUsHtss,
      clearEvaluatedCharges,
      primaryTariffCode,
      runTariffEvaluation,
    ],
  );

  const tariffsLength = tariffs?.length ?? 0;

  const handleClear = (index: number) => {
    const isPrimary = index === tariffsLength - 1;
    if (isPrimary) {
      form.setFieldValue(tariffFullNamePath, []);
      clearFields({
        form,
        fullNamePath: lineFullNamePath,
        exclude: LINE_LEVEL_PRESERVE,
      })();
    } else {
      form.setFields([
        { name: [...tariffFullNamePath, index, 'htsNumber'], value: undefined },
      ]);
    }
  };

  return (
    <Form.List name={tariffLocalNamePath} minimum={1}>
      {(fields, { add, remove }) => (
        <>
          {!readOnly &&
            primaryTariffCode &&
            htsData[formatHTS(primaryTariffCode)] && (
              <ProductValueRow>
                <Button size="small" onClick={() => add({}, 0)}>
                  {safeGetMessage(
                    bundle,
                    'add_duty_button',
                    undefined,
                    'Add duty',
                  )}
                </Button>
              </ProductValueRow>
            )}
          {fields.map((field, index) => {
            const allHtsNumbers: string[] = (tariffs ?? [])
              .map((tariff: any, tariffIndex: number) =>
                tariffIndex !== index ? tariff?.htsNumber : null,
              )
              .filter(Boolean);
            const tariffFormData = tariffs?.[index];
            const htsObj = tariffFormData?.htsNumber
              ? htsData[formatHTS(tariffFormData.htsNumber)]
              : undefined;
            const isPrimary = index === fields.length - 1;
            const formTariffType =
              tariffFormData?.tariffType ??
              tariffFormData?.additionalTariffType;
            const effectiveHtsObj: HtsObject | undefined = htsObj
              ? ({
                  ...htsObj,
                  additionalTariffType:
                    htsObj.additionalTariffType || formTariffType,
                } as HtsObject)
              : formTariffType
              ? ({
                  additionalTariffType: formTariffType,
                } as HtsObject)
              : undefined;
            const evaluated = isEvaluatedDutyDisplayEnabled
              ? getEvaluatedDuty(tariffFormData?.charges)
              : undefined;
            const showHtsFallback = isPrimary || !isEvaluatedDutyDisplayEnabled;
            const cappedRate = getSection232CappedAdValoremRate({
              htsNumber: tariffFormData?.htsNumber,
              primaryHtsObj,
              primaryHtsNumber: primaryTariffCode,
              lineValue: productValue,
              lineReporting: primaryTariff?.unitReportingQuantities,
            });
            // evaluated charge > 232 capped rate > raw HTS estimate (primary only)
            const dutyRate =
              evaluated?.rate ??
              (cappedRate !== undefined
                ? formatAdValoremPercent(cappedRate)
                : showHtsFallback
                ? formatDutyRate(htsObj)
                : undefined);
            const dutyValue =
              evaluated?.value ??
              (cappedRate !== undefined
                ? `$${((cappedRate / 100) * productValue).toFixed(2)}`
                : showHtsFallback
                ? computeDutyValue(htsObj, productValue)
                : undefined);
            return (
              <TariffRow key={field.key}>
                <Tooltip
                  title={htsObj?.fullText || htsObj?.text}
                  placement="topLeft"
                  mouseEnterDelay={0.5}
                >
                  <TariffRowLabel>
                    {getRowLabel(index, isPrimary, bundle, effectiveHtsObj)}
                  </TariffRowLabel>
                </Tooltip>
                <TariffRowContent>
                  <HTSNumberV2
                    field={[field.name, 'htsNumber']}
                    fullNamePath={[
                      ...tariffFullNamePath,
                      field.name,
                      'htsNumber',
                    ]}
                    required={required && isPrimary}
                    disabled={disabled}
                    readOnly={readOnly}
                    onSelect={
                      isPrimary
                        ? handlePrimarySelect
                        : handleSecondarySelect(index)
                    }
                    onClear={() => handleClear(index)}
                    excludeValues={allHtsNumbers}
                  />
                  <DutyInfo>
                    {dutyRate ? (
                      <span>
                        {safeGetMessage(
                          bundle,
                          'duty_rate',
                          { rate: dutyRate },
                          `Duty rate: ${dutyRate}`,
                        )}
                      </span>
                    ) : null}
                    {dutyValue ? (
                      <span>
                        {safeGetMessage(
                          bundle,
                          'duty_value',
                          { value: dutyValue },
                          `Duty value: ${dutyValue}`,
                        )}
                      </span>
                    ) : null}
                  </DutyInfo>
                  {!isPrimary && !readOnly && (
                    <RemoveButton
                      onClick={() => {
                        const current =
                          form.getFieldValue(tariffFullNamePath) || [];
                        form.setFieldValue(
                          tariffFullNamePath,
                          current.filter(
                            (_: unknown, i: number) => i !== index,
                          ),
                        );
                      }}
                    >
                      {safeGetMessage(
                        bundle,
                        'remove_button',
                        undefined,
                        'Remove',
                      )}
                    </RemoveButton>
                  )}
                  {isPrimary && showValidateButton && (
                    <Button onClick={onValidateHts} loading={validateLoading}>
                      {safeGetMessage(
                        bundle,
                        'validate_hts_button',
                        undefined,
                        'Validate HTS Number',
                      )}
                    </Button>
                  )}
                  {isPrimary && loading && <Spin size="small" />}
                </TariffRowContent>
              </TariffRow>
            );
          })}
        </>
      )}
    </Form.List>
  );
};

export default memo(LineItemTariffDisplayV2);
